import { useQuery } from '@tanstack/react-query';
import { apiGet } from '../services/api.ts';

export interface VideoStatusItem {
  scene_number: number;
  video_url: string | null;
  audio_url: string | null;
  prompt?: string;
  status: 'generating' | 'completed';
  audio_status: 'generating' | 'completed';
}

export function useVideoStatusQuery(
  storyId: number | null | undefined,
  _storyStatus?: { current_step?: string } // kept for signature parity
) {
  return useQuery<VideoStatusItem[], Error>({
    queryKey: ['video-status', storyId],
    queryFn: async () => {
      if (!storyId) return [];
      const res = await apiGet<VideoStatusItem[]>(`/runs/${storyId}/video-status`);
      if (res.error) throw new Error(res.error);
      return res.data ?? [];
    },
    enabled: !!storyId,               // <-- do not gate by step
    staleTime: 0,                     // <-- always stale so focus refetches (refreshes signed URLs)
    refetchOnWindowFocus: true,       // <-- refresh on tab focus
    refetchOnReconnect: true,
    refetchOnMount: 'always',         // <-- always refetch when component mounts
    gcTime: 5 * 60 * 1000,
    // Poll while generation is in progress; stop when everything is done.
    refetchInterval: (query) => {
      const data = query.state.data as VideoStatusItem[] | undefined;
      if (!data || data.length === 0) return 2000;
      const allDone = data.every(
        v => v.status === 'completed' && v.audio_status === 'completed' && !!v.video_url
      );
      return allDone ? false : 2000;
    },
    // Don't use placeholderData to avoid stale URLs
    // placeholderData: (prev) => prev,
    // or: keepPreviousData: true,
  });
}
